import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { PMAvatar, PMBreadcrumb, PMButton, PMCard, PMDialog, PMEmptyState, PMField, PMFormActions, PMSkeleton, PMTabs, PMBadge } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useAncestors, useChildren, useLocation, useLocationContacts, useReminders, useVisits } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { levelDef } from "@/lib/levels";
import { LocationMap } from "@/ui/LocationMap";
import { FieldVisitCard } from "@/ui/FieldVisitCard";
import { ConditionsPanel, WeatherChip } from "@/ui/Conditions";
import { Character } from "@/ui/Character";
import { CountUp } from "@/ui/motion";
import { dayKey, relTime } from "@/ui/format";
import { recency } from "./LocationsPage";
import "./locations.css";
import "../visits/visit.css";

export function LocationPage() {
  const { locationId, teamId } = useParams({ from: "/t/$teamId/locations/$locationId" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const loc = useLocation(locationId);
  const l = loc.data;
  const ancestors = useAncestors(locationId);
  const children = useChildren(locationId);
  const scope = [locationId, ...children.data.map((c) => c.id)];
  const visits = useVisits({ locationIds: scope });
  const contacts = useLocationContacts(locationId);
  const tasks = useReminders();
  const [tab, setTab] = useState("visits");
  const [adding, setAdding] = useState(false);

  if (!l) return loc.isLoading ? <div className="page"><PMSkeleton shape="rect" height={420} /></div> : <div className="page"><PMEmptyState title="Not found" description="This location isn't synced to this device." /></div>;

  const d = levelDef(l.level, hierarchy.key);
  const idx = hierarchy.levels.findIndex((x) => x.key === l.level);
  const childDef = hierarchy.levels[idx + 1];
  const scopeTasks = tasks.data.filter((t) => t.map_feature_id && scope.includes(t.map_feature_id));
  const open = scopeTasks.filter((t) => !t.completed_at && !t.canceled_at);
  const last = visits.data[0]?.started_at;
  const rc = recency(last);
  const obs = visits.data.reduce((s, v) => s + (v.obs_count ?? 0), 0);

  const crumbs = [
    { label: hierarchy.levels[0].plural, href: `/t/${teamId}/locations` },
    ...ancestors.data.map((a) => ({ label: a.label || "", href: `/t/${teamId}/locations/${a.id}` })),
    { label: l.label || "" },
  ];
  const mapItems = [
    { id: l.id, label: l.label || "", geometry: l.geometry, latitude: l.latitude, longitude: l.longitude, tone: "info" as const },
    ...children.data.map((c) => ({ id: c.id, label: c.label || "", geometry: c.geometry, latitude: c.latitude, longitude: c.longitude })),
  ];

  const hasPoint = !!(l.latitude && l.longitude);
  return (
    <div className="page page--wide location">
      <PMBreadcrumb items={crumbs} onNavigate={(it: { href?: string }) => it.href && nav({ to: it.href } as any)} />
      <section className="loc-hero2">
        <LocationMap items={mapItems} height="100%" onSelect={(id) => id !== l.id && nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: id } })} />
        <div className="loc-card pm-glass">
          <span className="loc-level">{d.emoji} {d.label}</span>
          <h1 className="vh-title">{l.label || "Untitled"}</h1>
          {l.description && <p className="muted" style={{ margin: 0 }}>{l.description}</p>}
          <div className="row" style={{ flexWrap: "wrap" }}>
            <PMBadge variant={rc.badge} dot>{last ? `Visited ${rc.label}` : "Never visited"}</PMBadge>
            {hasPoint && <WeatherChip lat={+l.latitude!} lng={+l.longitude!} />}
          </div>
          <div className="row"><PMButton size="sm" icon="add-circle" onClick={() => setAdding(true)}>Add task</PMButton></div>
        </div>
      </section>

      <div className="loc-stats2">
        <Stat emoji="📋" tone="tone-brand" value={visits.data.length} label="Field visits" />
        <Stat emoji="🔎" tone="tone-photo" value={obs} label="Observations" />
        {childDef && <Stat emoji={childDef.emoji} tone="tone-text" value={children.data.length} label={childDef.plural} />}
        <Stat emoji="✅" tone="tone-warn" value={open.length} label="Open tasks" />
      </div>

      <div className="vh-body">
        <div className="visit-main">
          <PMTabs value={tab} onChange={setTab} tabs={[
            { id: "visits", label: "Field Visits", count: visits.data.length },
            ...(childDef ? [{ id: "children", label: childDef.plural, count: children.data.length }] : []),
            { id: "tasks", label: "Tasks", count: open.length },
          ]} aria-label="Location sections" />
          {tab === "visits" && (visits.data.length ? <div className="visit-grid">{visits.data.map((v) => <FieldVisitCard key={v.id} v={v} onOpen={() => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: v.id } })} />)}</div>
            : <Character name="bukkie" size={110} title="No visits yet" line={`Visits to this ${d.label.toLowerCase()} appear here after they sync.`} />)}
          {tab === "children" && childDef && (
            <div className="child-grid">
              {children.data.map((c) => (
                <button key={c.id} className="child-card pm-lift" onClick={() => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: c.id } })}>
                  <div className="child-map"><LocationMap items={[{ id: c.id, label: c.label || "", geometry: c.geometry, latitude: c.latitude, longitude: c.longitude }]} height={140} interactive={false} /></div>
                  <div className="child-body"><span className="list-title">{childDef.emoji} {c.label}</span><span className="meta">{c.description}</span></div>
                </button>
              ))}
              {!children.data.length && <PMEmptyState size="sm" title={`No ${childDef.plural.toLowerCase()}`} />}
            </div>
          )}
          {tab === "tasks" && (
            <div className="card list">
              {scopeTasks.map((t) => (
                <div key={t.id} className="list-row" style={{ cursor: "default" }}>
                  <span>{t.completed_at ? "✓" : "◌"}</span>
                  <span className="list-main"><span className="list-title">{t.details}</span><span className="meta">{t.location_label}<span className="sep" />{t.reminder_date ? relTime(t.reminder_date) : "No date"}</span></span>
                </div>
              ))}
              {!scopeTasks.length && <Character name="happy-propal" size={80} title="No tasks" line="Follow-ups from visits land here." />}
            </div>
          )}
        </div>
        <aside className="sticky-col">
          {hasPoint && (
            <PMCard eyebrow="Field conditions" icon="leaf-outline">
              <ConditionsPanel lat={+l.latitude!} lng={+l.longitude!} />
            </PMCard>
          )}
          <PMCard eyebrow="Contacts" icon="user-outline" count={contacts.data.length}>
            {contacts.data.length ? contacts.data.map((c) => (
              <div className="contact-mini" key={c.id}>
                <PMAvatar name={c.name || "?"} size={36} />
                <div><strong>{c.name}</strong><div className="meta">{c.role}{c.phone && <><span className="sep" /><a href={`tel:${c.phone}`}>{c.phone}</a></>}</div>{c.email && <a className="hint" href={`mailto:${c.email}`}>{c.email}</a>}</div>
              </div>
            )) : <p className="hint">No contacts linked.</p>}
          </PMCard>
        </aside>
      </div>

      <AddTask open={adding} onClose={() => setAdding(false)} locationId={l.id} />
    </div>
  );
}

function Stat({ emoji, tone, value, label }: { emoji: string; tone: string; value: number; label: string }) {
  return <div className="stat"><span className={`stat-ic ${tone}`}>{emoji}</span><span><span className="stat-val"><CountUp value={value} /></span><span className="stat-lbl" style={{ display: "block" }}>{label}</span></span></div>;
}

function AddTask({ open, onClose, locationId }: { open: boolean; onClose: () => void; locationId: string }) {
  const m = useMutations();
  const [details, setDetails] = useState("");
  const [date, setDate] = useState(dayKey(new Date(Date.now() + 86_400_000).toISOString()));
  const save = async () => { if (!details.trim()) return; if (await m.createReminder({ locationId, details: details.trim(), date })) { setDetails(""); onClose(); } };
  return (
    <PMDialog open={open} onClose={onClose} title="Add task" maxWidth={520} footer={<PMFormActions onCancel={onClose} onConfirm={save} confirmLabel="Add task" confirmDisabled={!details.trim()} />}>
      <div className="sign-form">
        <PMField label="Task" value={details} onChange={(e: any) => setDetails(e.target.value)} placeholder="Re-scout for aphids" autoFocus />
        <PMField label="Due" type="date" value={date} onChange={(e: any) => setDate(e.target.value)} />
      </div>
    </PMDialog>
  );
}
